import { useParams } from 'react-router-dom';
import { RiskPlusPageShell, SectionCard, RiskBadge, riskColor } from './RiskPlusPageShell';
import { useRiskPlusPageData } from './useRiskPlusPageData';
import InternalRiskRadarChart from '../RiskPlusReport/InternalRiskRadarChart';
import ExternalRiskRadarChart from '../RiskPlusReport/ExternalRiskRadarChart';
import type { RiskPlusReport } from '../../entities/report/RiskPlusReport';
import './report.css';

function ImpactRow({
  label,
  harmRank,
  description,
}: {
  label: string;
  harmRank: string;
  description: string;
}) {
  const rank = harmRank as Parameters<typeof riskColor>[0];
  return (
    <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-soft-gray px-4 py-3">
      <span
        className="mt-0.5 h-3 w-3 shrink-0 rounded-full"
        style={{ background: riskColor(rank) }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-midnight-navi">{label}</span>
          <RiskBadge rank={rank} />
        </div>
        {description && (
          <p className="mt-1 text-xs leading-relaxed text-midnight-navi">{description}</p>
        )}
      </div>
    </div>
  );
}

function InternalConcentrationCard({ report }: { report: RiskPlusReport }) {
  const { internal } = report.deploymentProfile.aiSystemImpact;

  return (
    <SectionCard title="Internal Risk Concentration - Harm Categories">
      <p className="text-xs text-midnight-navi leading-relaxed">
        Distribution of inherent risk across the four institutional harm categories - Financial,
        Legal, Reputational, and Operational - for the deploying organisation.
      </p>
      <div className="mt-4 flex flex-col items-center">
        <InternalRiskRadarChart reportData={report} />
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        <ImpactRow label="Financial" harmRank={internal.financial.harmRank} description={internal.financial.description} />
        <ImpactRow label="Reputational" harmRank={internal.reputational.harmRank} description={internal.reputational.description} />
        <ImpactRow label="Operational" harmRank={internal.operational.harmRank} description={internal.operational.description} />
        <ImpactRow label="Legal" harmRank={internal.legal.harmRank} description={internal.legal.description} />
      </div>
    </SectionCard>
  );
}

function ExternalConcentrationCard({ report }: { report: RiskPlusReport }) {
  const { external } = report.deploymentProfile.aiSystemImpact;

  return (
    <SectionCard title="External Risk Concentration - Stakeholder Groups">
      <p className="text-xs text-midnight-navi leading-relaxed">
        Inherent risk exposure mapped across three external stakeholder groups who may be impacted
        by this deployment.
      </p>
      <div className="mt-4 flex flex-col items-center">
        <ExternalRiskRadarChart reportData={report} />
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        <ImpactRow label="People" harmRank={external.people.harmRank} description={external.people.description} />
        <ImpactRow label="Organisations" harmRank={external.organisations.harmRank} description={external.organisations.description} />
        <ImpactRow label="Ecosystems" harmRank={external.ecosystems.harmRank} description={external.ecosystems.description} />
      </div>
    </SectionCard>
  );
}

function ConcentrationSummary({ report }: { report: RiskPlusReport }) {
  const text = report.deploymentProfile.riskConcentration;
  if (!text) return null;

  return (
    <SectionCard title="Risk Concentration Summary">
      <p className="text-xs leading-relaxed text-midnight-navi">{text}</p>
    </SectionCard>
  );
}


const RiskPlusRiskConcentration = () => {
  const { useCaseChatId = '' } = useParams<{ useCaseChatId: string }>();
  const data = useRiskPlusPageData(useCaseChatId);

  return (
    <RiskPlusPageShell useCaseChatId={useCaseChatId} data={data}>
      {(report) => (
        <>
          <ConcentrationSummary report={report} />
          <InternalConcentrationCard report={report} />
          <ExternalConcentrationCard report={report} />
        </>
      )}
    </RiskPlusPageShell>
  );
};

export default RiskPlusRiskConcentration;
